'use strict';
import findIndex from 'lodash/findIndex';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import set from 'lodash/set';
import * as constants from 'vd-constants';

/**
 * Callback to handle the business logic during a polling cycle.
 * IMPORTANT: One of the callbacks (stopCallback, continueCallback or retryCallback) must be called.
 * @callback ProcessCallback
 * @param {string} eventId
 * @param {Function} continueCallback - Callback to continue polling for event. Called with continueCallback([timeout]).
 * @param {Function} stopCallback - Callback to stop polling for event. Called with stopCallback().
 * @param {Function} retryCallback - Callback to continue polling for event until number of retries reaches max allowed attempts. Called with retryCallback([timeout]).
 * @param {Record<DeviceId, string>} accessibleAddresses - Accessible addresses map for the devices at the site
 */

/**
 * Returned options from the process callback.
 * @typedef {Object} ProcessOptions
 * @property {boolean} [retry] - Indicates whether process has entered a retry cycle because of an error (true), or a normal polling cycle (false)
 * @property {number} [timeout] - Timeout (in milliseconds) before doing next polling cycle. Used when overriding default timeout/interval for polling cycles.
 */

/**
 * Custom options when starting polling for an event.
 * @typedef {Object} PollingOptions
 * @property {number} [interval] - Default interval (in milliseconds) between each polling cycle
 * @property {number} [maxRetries] - Maximum number of retry attempts when polling business logic determines an error has occurred
 */

/**
 * Find the index to the event in the stop queue.
 * @param {DevicePollHandler} instance
 * @param {string} eventId
 * @returns {number} Returns index of event ID in stop queue, otherwise -1 if not found
 */
const findEventInStopQueue = function findEventInStopQueue(instance, eventId) {
  return findIndex(instance.queueStop, (id) => {
    return id === eventId;
  });
};

/**
 * Make a poll stop request for the event, i.e. add event to the stop queue so that polling will stop on the next poll cycle.
 * @param {DevicePollHandler} instance
 * @param {DeviceId} deviceId
 * @param {string} eventId
 */
const addToStopQueue = function addToStopQueue(instance, deviceId, eventId) {
  // Store ID for event currently being polled that should stopped being polled on the next round
  if (
    !isNil(eventId) &&
    findEventInStopQueue(instance, eventId) === -1 &&
    findEventInLocationQueue(instance, deviceId, eventId) !== -1
  ) {
    instance.queueStop.push(eventId);
  }
};

/**
 * Cancel the poll stop request for the event, i.e. remove event from the stop queue.
 * @param {DevicePollHandler} instance
 * @param {string} eventId
 */
const removeFromStopQueue = function removeFromStopQueue(instance, eventId) {
  const index = findEventInStopQueue(instance, eventId);
  if (index !== -1) {
    instance.queueStop.splice(index, 1);
  }
};

/**
 * Find the index to the event in the device polling queue (device-specific).
 * @param {DevicePollHandler} instance
 * @param {DeviceId} deviceId
 * @param {string} eventId
 * @returns {number} Returns index of event ID in device polling queue, otherwise -1 if not found
 */
const findEventInLocationQueue = function findEventInLocationQueue(instance, deviceId, eventId) {
  return !has(instance.queuePerDevice, [deviceId])
    ? -1
    : findIndex(instance.queuePerDevice[deviceId], (id) => {
        return id === eventId;
      });
};

/**
 * Add event to the device polling queue (if not already in the queue).
 * @param {DevicePollHandler} instance
 * @param {DeviceId} deviceId
 * @param {string} eventId
 * @param {boolean} [append] - Add event to end of device polling queue (true), or add event to start of device polling queue (false)
 */
const addToLocationQueue = function addToLocationQueue(
  instance,
  deviceId,
  eventId,
  accessibleAddress,
  append = true,
) {
  if (isEmptyLocationQueue(instance, deviceId)) {
    // Device not being polled at the moment
    instance.queuePerDevice[deviceId] = [eventId];

    // Start polling device immediately
    processLocationQueue(instance, deviceId, accessibleAddress);
  } else if (findEventInLocationQueue(instance, deviceId, eventId) === -1) {
    // Event ID doesn't exist in device queue, so add it
    if (append) {
      instance.queuePerDevice[deviceId].push(eventId);
    } else {
      instance.queuePerDevice[deviceId].unshift(eventId);
    }
  }
};

/**
 * Remove event from the device polling queue.
 * @param {DevicePollHandler} instance
 * @param {DeviceId} deviceId
 * @param {string} eventId
 */
const removeFromLocationQueue = function removeFromLocationQueue(instance, deviceId, eventId) {
  const index = findEventInLocationQueue(instance, deviceId, eventId);
  if (index !== -1) {
    instance.queuePerDevice[deviceId].splice(index, 1);
  }
};

/**
 * Check if the device polling queue is empty for the provided device.
 * @param {DevicePollHandler} instance
 * @param {DeviceId} deviceId
 * @returns {boolean}
 */
const isEmptyLocationQueue = function isEmptyLocationQueue(instance, deviceId) {
  const hasQueue =
    has(instance.queuePerDevice, [deviceId]) && instance.queuePerDevice[deviceId].length > 0;
  return !hasQueue;
};

/**
 * Start polling the device.
 * @param {DevicePollHandler} instance
 * @param {DeviceId} deviceId
 * @param {string | null} accessibleAddress
 * @param {number} [retryCount]
 * @param {number} [timeout]
 */
const startPollingLocationQueue = function startPollingLocationQueue(
  instance,
  deviceId,
  accessibleAddress,
  retryCount = 0,
  timeout = instance.defaultInterval,
) {
  setTimeout(() => {
    processLocationQueue(instance, deviceId, accessibleAddress, retryCount);
  }, timeout);
};

/**
 * Continue polling for the event (if no stop polling request was made).
 * @param {DevicePollHandler} instance
 * @param {DeviceId} deviceId
 * @param {string} eventId
 * @param {string | null} accessibleAddress
 * @param {number} [retryCount]
 * @param {number} [timeout]
 */
const continuePollingEventOnLocationQueue = function continuePollingEventOnLocationQueue(
  instance,
  deviceId,
  eventId,
  accessibleAddress,
  retryCount,
  timeout,
) {
  if (findEventInStopQueue(instance, eventId) !== -1) {
    stopPollingEventOnLocationQueue(instance, deviceId, eventId, accessibleAddress);
  } else {
    // Continue polling, but move event to back of queue first
    removeFromLocationQueue(instance, deviceId, eventId);
    instance.queuePerDevice[deviceId].push(eventId);

    startPollingLocationQueue(instance, deviceId, accessibleAddress, retryCount, timeout);
  }
};

/**
 * Stop polling for the event (note that polling will continue for other events on the device polling queue).
 * @param {DevicePollHandler} instance
 * @param {DeviceId} deviceId
 * @param {string} eventId
 * @param {string | null} accessibleAddress
 */
const stopPollingEventOnLocationQueue = function stopPollingEventOnLocationQueue(
  instance,
  deviceId,
  eventId,
  accessibleAddress,
) {
  removeFromLocationQueue(instance, deviceId, eventId);
  removeFromStopQueue(instance, eventId);

  if (!isEmptyLocationQueue(instance, deviceId)) {
    // Continue polling other events
    startPollingLocationQueue(instance, deviceId, accessibleAddress);
  }
};

/**
 * Process the first event on the device polling queue for the provided device.
 * @param {DevicePollHandler} instance
 * @param {DeviceId} deviceId
 * @param {string | null} accessibleAddress
 * @param {number} [retryCount]
 */
const processLocationQueue = function processLocationQueue(
  instance,
  deviceId,
  accessibleAddress,
  retryCount = 0,
) {
  if (!isEmptyLocationQueue(instance, deviceId)) {
    // Use first element from the queue
    const eventId = get(instance.queuePerDevice, [deviceId, 0]);

    if (!isEmpty(eventId) && isFunction(instance.processCallback)) {
      instance.processCallback(
        eventId,
        continuePollingEventOnLocationQueue.bind(
          null,
          instance,
          deviceId,
          eventId,
          accessibleAddress,
          0,
        ),
        stopPollingEventOnLocationQueue.bind(null, instance, deviceId, eventId, accessibleAddress),
        (timeout) => {
          // Error handler
          if (retryCount >= instance.defaultMaxRetries) {
            stopPollingEventOnLocationQueue(instance, deviceId, eventId, accessibleAddress);
          } else {
            retryCount++;
            continuePollingEventOnLocationQueue(
              instance,
              deviceId,
              eventId,
              accessibleAddress,
              retryCount,
              timeout,
            );
          }
        },
        instance.accessibleAddresses,
      );
    } else {
      throw new Error('Polling Error: Process callback not set or no event ID');
    }
  }
};

class DevicePollHandler {
  constructor() {
    this.queuePerDevice = {};

    // Array of event IDs
    this.queueStop = [];

    this.processCallback = null;
    this.defaultInterval = null;
    this.defaultMaxRetries = null;

    this.trackerPerDevice = {};
    this.accessibleAddresses = null;
  }

  /**
   * Start polling for the event.
   * @param {string} eventId
   * @param {EventCamera[]} eventCameras - Event cameras
   * @param {Record<DeviceId, string>} accessibleAddresses - Accessible addresses map for the devices at the site
   * @param {Record<string, CallhomeCamera>} siteCamerasById - Site cameras by ID
   * @param {ProcessCallback} callback - Callback function that handles business logic during a polling cycle.
   * @param {PollingOptions} options
   * @param {string} [tracker='']
   */
  startPollingEvent(
    eventId,
    eventCameras,
    accessibleAddresses,
    siteCamerasById,
    callback,
    options = {},
    tracker = '',
  ) {
    // TODO In future if we ever have to poll Connect (local VMS) for other features (i.e. other endpoints), these properties will need to be stored per event (and polling type). Since we should only be polling Connect with one thing at a time to avoid load.
    this.processCallback = callback;
    this.defaultInterval = options.interval || constants.POLLING_DEFAULT_INTERVAL_MS;
    this.defaultMaxRetries = options.maxRetries || constants.POLLING_DEFAULT_MAX_RETRY_ATTEMPTS;
    this.accessibleAddresses = accessibleAddresses;

    for (const camera of eventCameras) {
      const deviceId = siteCamerasById[camera.chId]?.deviceId;
      const accessibleAddress = accessibleAddresses[deviceId];

      if (!isEmpty(tracker) && !has(this.trackerPerDevice, [deviceId, eventId, tracker])) {
        set(this.trackerPerDevice, [deviceId, eventId, tracker], true);
      }

      removeFromStopQueue(this, eventId);
      addToLocationQueue(this, deviceId, eventId, accessibleAddress);
    }
  }

  /**
   * Stop polling for the provided event (note that it will stop during the next polling cycle).
   * @param {string} eventId
   * @param {EventCamera[]} eventCameras - Event cameras
   * @param {Record<string, CallhomeCamera>} siteCamerasById - Site cameras by ID
   * @param {string} [tracker='']
   */
  stopPollingEvent(eventId, eventCameras, siteCamerasById, tracker = '') {
    for (const camera of eventCameras) {
      const deviceId = siteCamerasById[camera.chId]?.deviceId;

      if (!isEmpty(tracker) && has(this.trackerPerDevice, [deviceId, eventId, tracker])) {
        delete this.trackerPerDevice[deviceId][eventId][tracker];
      }

      if (
        !has(this.trackerPerDevice, [deviceId, eventId]) ||
        isEmpty(this.trackerPerDevice[deviceId][eventId])
      ) {
        addToStopQueue(this, deviceId, eventId);
      }
    }
  }

  /**
   * Stop polling for all events (note that it will stop during the next polling cycle).
   * @param {string} [excludeEventId] - Exclude provided event, i.e. continue polling for only this event (if it's in the device polling queue).
   */
  stopPollingAllEvents(excludeEventId) {
    forEach(this.queuePerDevice, (events, deviceId) => {
      forEach(events, (eventId) => {
        if (isNil(excludeEventId) || eventId !== excludeEventId) {
          if (has(this.trackerPerDevice, [deviceId, eventId])) {
            delete this.trackerPerDevice[deviceId][eventId];
          }

          addToStopQueue(this, deviceId, eventId);
        }
      });
    });
  }
}

export default DevicePollHandler;
